/** Ajuste de facturación: lo que Contabilidad cobra, sin reescribir el anexo firmado. */

export const BILLING_ADJUSTMENT_STAGES = new Set(['IMPLEMENTATION', 'BILLING']);

const money = (value) => Math.round((Number(value) || 0) * 100) / 100;

const skuOf = (lic) => String(lic?.sku || lic?.catalogSku || '').trim();

export function hasBillingAdjustment(contract) {
  return contract?.billingAdjustment?.active === true;
}

export function canEditBillingAdjustment(contract, { readOnly = false } = {}) {
  if (readOnly || !contract) return false;
  if (contract.facturado === true || contract.pipelineStage === 'CLOSED') return false;
  if (contract.billingAdjustment?.locked === true) return false;
  return BILLING_ADJUSTMENT_STAGES.has(contract.pipelineStage);
}

export function captureSignedCommercial(contract = {}) {
  const anexo = contract.anexo || {};
  const includeIva = contract.includeIva === false || contract.proposal?.includeIva === false
    ? false
    : true;
  return {
    licencias: (Array.isArray(contract.licencias) ? contract.licencias : [])
      .map((lic) => ({
        sku: skuOf(lic),
        skuLabel: String(lic?.skuLabel || lic?.name || lic?.sku || '').trim(),
        qty: Number(lic?.qty) || 0,
        descuento: Number(lic?.descuento) || 0,
        precioUnitario: money(lic?.precioUnitario),
      }))
      .filter((lic) => lic.sku),
    serviciosAdicionales: normalizeServicios(contract.serviciosAdicionales),
    billingFrequency: anexo.billingFrequency || contract.billingFrequency || 'Mensual',
    vigenciaLabel: anexo.vigenciaLabel || 'Flexible',
    includeIva,
    currency: anexo.currency || contract.currency || 'MXN',
  };
}

export function normalizeLicencias(list) {
  return (Array.isArray(list) ? list : [])
    .map((lic) => ({
      sku: skuOf(lic),
      skuLabel: String(lic?.skuLabel || lic?.name || lic?.sku || '').trim(),
      qty: Number(lic?.qty) || 0,
      descuento: Number(lic?.descuento) || 0,
      precioUnitario: money(lic?.precioUnitario),
      billingPriceLocked: true,
    }))
    .filter((lic) => lic.sku && lic.qty > 0);
}

export function normalizeServicios(list) {
  return (Array.isArray(list) ? list : [])
    .map((serv) => ({
      ...(serv?.id ? { id: serv.id } : {}),
      name: String(serv?.name || '').trim(),
      qty: Number(serv?.qty) || 1,
      price: money(serv?.price),
      descuento: Number(serv?.descuento) || 0,
    }))
    .filter((serv) => serv.name);
}

function activeLicencias(list) {
  return (Array.isArray(list) ? list : []).filter((lic) => skuOf(lic) && (Number(lic.qty) || 0) > 0);
}

function sameSkuSet(left, right) {
  const a = left.map(skuOf).filter(Boolean).sort();
  const b = right.map(skuOf).filter(Boolean).sort();
  if (a.length !== b.length) return false;
  return a.every((sku, index) => sku === b[index]);
}

/** Vigencia, frecuencia, producto o precio de lista. Cantidad, descuento y servicios de un solo pago no. */
export function adjustmentChangesAnnex(signed, draft) {
  if (!signed || !draft) return false;
  const signedFrequency = signed.billingFrequency || 'Mensual';
  const nextFrequency = draft.billingFrequency || signedFrequency;
  if (nextFrequency !== signedFrequency) return true;
  const signedLines = activeLicencias(signed.licencias);
  const nextLines = normalizeLicencias(draft.licencias);
  if (!sameSkuSet(signedLines, nextLines)) return true;
  const bySku = new Map(nextLines.map((lic) => [skuOf(lic), lic]));
  return signedLines.some((lic) => {
    const next = bySku.get(skuOf(lic));
    if (!next) return true;
    return Math.abs(money(lic.precioUnitario) - money(next.precioUnitario)) > 0.5;
  });
}

function fingerprint(source) {
  const licencias = activeLicencias(source.licencias)
    .map((lic) => `${skuOf(lic)}|${Number(lic.qty) || 0}|${money(lic.precioUnitario)}|${Number(lic.descuento) || 0}`)
    .sort()
    .join(';');
  const servicios = normalizeServicios(source.serviciosAdicionales)
    .map((serv) => `${serv.name}|${serv.qty}|${serv.price}|${serv.descuento}`)
    .sort()
    .join(';');
  return [
    source.billingFrequency || 'Mensual',
    source.includeIva !== false ? '1' : '0',
    licencias,
    servicios,
  ].join('||');
}

export function billingNumbersDiffer(signed, draft) {
  const next = {
    billingFrequency: draft.billingFrequency || signed.billingFrequency || 'Mensual',
    includeIva: draft.includeIva !== false,
    licencias: normalizeLicencias(draft.licencias),
    serviciosAdicionales: normalizeServicios(draft.serviciosAdicionales),
  };
  const base = {
    billingFrequency: signed.billingFrequency || 'Mensual',
    includeIva: signed.includeIva !== false,
    licencias: activeLicencias(signed.licencias),
    serviciosAdicionales: normalizeServicios(signed.serviciosAdicionales),
  };
  return fingerprint(base) !== fingerprint(next);
}

export function buildBillingAdjustment({ signed, draft, actor = {}, existing = null }) {
  const reason = String(draft?.reason || '').trim();
  if (reason.length < 8) {
    throw new Error('Escribe la razón del ajuste, para que contabilidad sepa qué cambió.');
  }
  const instruction = String(draft?.instruction || '').trim();
  const licencias = normalizeLicencias(draft?.licencias);
  const serviciosAdicionales = normalizeServicios(draft?.serviciosAdicionales);
  if (!licencias.length && !serviciosAdicionales.length && !instruction) {
    throw new Error('Agrega un cambio en las líneas o una nota para contabilidad.');
  }
  const signedSnapshot = existing?.signedSnapshot || signed;
  if (!signedSnapshot) throw new Error('No hay propuesta firmada para comparar.');
  const numbersChanged = billingNumbersDiffer(signedSnapshot, draft || {});
  if (!numbersChanged && !instruction) {
    throw new Error('El ajuste es igual a lo firmado. Cambia un monto o deja una nota para contabilidad.');
  }
  const now = new Date().toISOString();
  const name = String(actor.name || '').trim();
  const email = String(actor.email || '').trim().toLowerCase();
  return {
    active: true,
    locked: false,
    reason,
    ...(instruction ? { instruction } : {}),
    requestedByName: existing?.requestedByName || name,
    requestedByEmail: existing?.requestedByEmail || email,
    requestedAt: existing?.requestedAt || now,
    updatedAt: now,
    updatedByName: name,
    updatedByEmail: email,
    billingFrequency: draft.billingFrequency || signedSnapshot.billingFrequency || 'Mensual',
    includeIva: draft.includeIva !== false,
    currency: signedSnapshot.currency || 'MXN',
    changesAnnex: adjustmentChangesAnnex(signedSnapshot, draft || {}),
    signedSnapshot,
    licencias,
    serviciosAdicionales,
  };
}

/** Lo firmado, solo para que Contabilidad lo compare. No es el monto a facturar. */
export function contractForSignedReference(contract) {
  const snap = contract?.billingAdjustment?.signedSnapshot;
  if (!hasBillingAdjustment(contract) || !snap) return null;
  const frequency = snap.billingFrequency || contract.anexo?.billingFrequency || contract.billingFrequency || 'Mensual';
  const includeIva = snap.includeIva !== false;
  return {
    ...contract,
    includeIva,
    billingFrequency: frequency,
    licencias: (snap.licencias || []).map((lic) => ({ ...lic, billingPriceLocked: true })),
    serviciosAdicionales: Array.isArray(snap.serviciosAdicionales) ? snap.serviciosAdicionales : [],
    anexo: {
      ...(contract.anexo || {}),
      billingFrequency: frequency,
      vigenciaLabel: snap.vigenciaLabel || contract.anexo?.vigenciaLabel,
    },
    proposal: {
      ...(contract.proposal || {}),
      includeIva,
    },
  };
}

/** Copia que lee Contabilidad. El contrato guardado no se modifica. */
export function contractForBilling(contract) {
  if (!hasBillingAdjustment(contract)) return contract;
  const adj = contract.billingAdjustment;
  const frequency = adj.billingFrequency || contract.anexo?.billingFrequency || contract.billingFrequency || 'Mensual';
  const includeIva = adj.includeIva !== false;
  return {
    ...contract,
    includeIva,
    billingFrequency: frequency,
    licencias: (adj.licencias || []).map((lic) => ({ ...lic, billingPriceLocked: true })),
    serviciosAdicionales: Array.isArray(adj.serviciosAdicionales) ? adj.serviciosAdicionales : [],
    anexo: {
      ...(contract.anexo || {}),
      billingFrequency: frequency,
    },
    proposal: {
      ...(contract.proposal || {}),
      includeIva,
    },
  };
}

export function billingLicenseUnit(lic, resolvedUnit) {
  if (lic?.billingPriceLocked && Number(lic.precioUnitario) > 0) return Number(lic.precioUnitario);
  return Number(resolvedUnit) || 0;
}

export function billingLicenseDiscount(lic, disableLicenseDiscount) {
  if (lic?.billingPriceLocked) return Number(lic.descuento) || 0;
  if (disableLicenseDiscount) return 0;
  return Number(lic.descuento) || 0;
}

export function lockBillingAdjustmentOnInvoice(contract, now = new Date().toISOString()) {
  const adj = contract?.billingAdjustment;
  if (!adj?.active || adj.locked) return null;
  return { ...adj, locked: true, lockedAt: now };
}

export function billingAdjustmentConfirmText(contract, total) {
  const adj = contract?.billingAdjustment;
  if (!adj?.active) return '';
  const amount = Number(total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const lines = ['Hay un ajuste de facturación.', '', adj.reason];
  if (adj.instruction) lines.push('', adj.instruction);
  if (adj.changesAnnex) {
    lines.push('', 'El precio o el producto no coinciden con el anexo firmado. El contrato no se reescribió.');
  }
  lines.push('', `Monto a facturar: $${amount}.`, '', '¿Ya quedó así en la factura?');
  return lines.join('\n');
}
